/**
 * Task Table Columns Component
 *
 * This module defines the column configuration for the tasks table/grid.
 * It exports a factory function `createTaskTableColumns` that generates column definitions
 * for displaying task data in a structured format.
 *
 * The columns include:
 * - Checkbox: Task selection and completion toggle
 * - Task Name: The task title and metadata
 * - Constituent: Associated contact information
 * - Due Date: When the task is due
 * - Action Type: The type of action required
 * - Assignee: Who is assigned to the task
 * - Created By: Who created the task
 * - Completed At: When the task was completed
 * - Actions: Add to calendar and delete options
 */

import {
  TaskCheckboxCell,
  TaskNameCell,
  TaskConstituentCell,
  TaskDueDateCell,
  TaskActionTypeCell,
  TaskCreatorCell,
  TaskAssigneeCell,
  TaskActionsCell,
} from './cells';

import { formattedDate } from 'apps/tasks/utils/utils';

export const createTaskTableColumns = ({
  user,
  taskFilters,
  cadenceNameMap = {},
  cadenceV2Enabled = false,
  onTaskNameClick,
  onActionTypeClick,
  onAddToCalendar,
  onDelete,
  toggleCompletionStatus,
  parseActionType,
  trackOutreachAction,
  addDrawer,
  openPinnedCommentModal,
  closePinnedCommentModal,
  isPinnedCommentModalOpen,
  ThankviewCTA,
  getContactEmailAddress,
}) => {
  const baseColumns = [
    {
      field: 'completion',
      headerName: 'Complete',
      description: 'Task completion toggle',
      sortable: false,
      hideable: false,
      align: 'center',
      headerAlign: 'center',
      width: 90,
      renderCell: ({ row }) => <TaskCheckboxCell task={row} user={user} onToggleCompletion={toggleCompletionStatus} />,
    },
    {
      field: 'title',
      headerName: 'Task Name',
      description: 'The task title',
      minWidth: 250,
      flex: 1,
      sortable: true,
      hideable: false,
      disableColumnResize: false,
      renderCell: (params) => (
        <TaskNameCell
          task={params.row}
          onClick={() => onTaskNameClick(params.row)}
          cadenceNameMap={cadenceNameMap}
          cadenceV2Enabled={cadenceV2Enabled}
        />
      ),
    },
    {
      field: 'target_contact_name',
      headerName: 'Constituent',
      description: 'The constituent associated with this task',
      minWidth: 180,
      flex: 1,
      sortable: true,
      renderCell: (params) => <TaskConstituentCell task={params.row} />,
    },
    {
      field: 'current_due_date',
      headerName: 'Due Date',
      description: 'When the task is due',
      minWidth: 120,
      flex: 0.8,
      sortable: true,
      renderCell: (params) => <TaskDueDateCell task={params.row} />,
    },
    {
      field: 'action_type',
      headerName: 'Action Type',
      description: 'The type of action required',
      sortable: true,
      minWidth: 140,
      flex: 0.8,
      renderCell: (params) => (
        <TaskActionTypeCell
          task={params.row}
          user={user}
          onActionTypeClick={onActionTypeClick}
          parseActionType={parseActionType}
          trackOutreachAction={trackOutreachAction}
          addDrawer={addDrawer}
          openPinnedCommentModal={openPinnedCommentModal}
          closePinnedCommentModal={closePinnedCommentModal}
          isPinnedCommentModalOpen={isPinnedCommentModalOpen}
          ThankviewCTA={ThankviewCTA}
          getContactEmailAddress={getContactEmailAddress}
        />
      ),
    },
    {
      field: 'owner_user_name',
      headerName: 'Assignee',
      description: 'Who is assigned to the task',
      minWidth: 150,
      flex: 0.8,
      sortable: true,
      renderCell: (params) => <TaskAssigneeCell task={params.row} />,
    },
    {
      field: 'creator_user_name',
      headerName: 'Created By',
      description: 'Who created the task',
      minWidth: 150,
      flex: 0.8,
      sortable: true,
      renderCell: (params) => <TaskCreatorCell task={params.row} />,
    },
    {
      field: 'completed_at',
      headerName: 'Completed Date',
      description: 'When the task was completed',
      sortable: true,
      minWidth: 140,
      flex: 0.8,
      valueFormatter: (params) => {
        return !!params ? formattedDate(params) : '';
      },
    },
    {
      field: 'actions',
      headerName: '',
      description: 'Task actions (add to calendar, delete)',
      sortable: false,
      hideable: false,
      width: 120,
      renderCell: (params) => (
        <TaskActionsCell task={params.row} user={user} onAddToCalendar={onAddToCalendar} onDelete={onDelete} />
      ),
    },
  ];

  return baseColumns;
};
